import React, {
	cloneElement,
	forwardRef,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
// @ts-ignore
import useEventOutside from '@omtanke/react-use-event-outside';
import useDarkMode from '../../../Hooks/useDarkMode';
import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/react';

export const DropdownToggle = ({
	children,
	isOpen,
	setIsOpen = () => {},
	hasIcon = true,
	buttonRef,
}) => {
	const setButtonRef = useCallback(
		(node) => {
			if (buttonRef) {
				if (typeof buttonRef === 'function') buttonRef(node);
				else if (buttonRef && typeof buttonRef === 'object') buttonRef.current = node;
			}
		},
		[buttonRef],
	);

	return cloneElement(
		children?.props?.isButtonGroup ? (
			<span className="visually-hidden">Toggle Dropdown</span>
		) : (
			children
		),
		{
			ref: setButtonRef,
			onClick: () => {
				if (children?.props?.onClick) children.props.onClick();
				if (setIsOpen) setIsOpen(!isOpen);
			},
			className: classNames(
				{
					'dropdown-toggle': hasIcon,
					'dropdown-toggle-split': children?.props?.isButtonGroup,
					show: isOpen,
				},
				children?.props?.className,
			),
			'aria-expanded': isOpen,
		},
	);
};
DropdownToggle.displayName = 'DropdownToggle';

export const DropdownMenu = ({
	isOpen,
	setIsOpen = () => {},
	children,
	className,
	isAlignmentEnd,
	breakpoint,
	size,
	direction,
	isCloseAfterLeave = true,
	buttonRef,
	...props
}) => {
	const dropdownListRef = useRef(null);

	const { x, y, refs, strategy, placement } = useFloating({
		placement: breakpoint
			? 'bottom-start'
			: `${
					(direction === 'up' && 'top') ||
					(direction === 'end' && 'right') ||
					(direction === 'start' && 'left') ||
					'bottom'
				}-${isAlignmentEnd ? 'end' : 'start'}`,
		middleware: [offset(1), flip(), shift()],
		whileElementsMounted: autoUpdate,
	});

	useEffect(() => {
		if (buttonRef && typeof buttonRef === 'object' && buttonRef.current) {
			refs.setReference(buttonRef.current);
		}
	}, [buttonRef, refs]);

	const { darkModeStatus } = useDarkMode();

	if (isOpen) {
		return (
			<ul
				role="presentation"
				ref={(node) => {
					dropdownListRef.current = node;
					refs.setFloating(node);
				}}
				style={{
					position: strategy,
					top: y ?? 0,
					left: x ?? 0,
				}}
				data-placement={placement}
				className={classNames(
					'dropdown-menu',
					'show',
					{ 'dropdown-menu-dark': darkModeStatus },
					{
						[`dropdown-menu-${size}`]: size,
						'dropdown-menu-end': !isAlignmentEnd && breakpoint,
						[`dropdown-menu${breakpoint ? `-${breakpoint}` : ''}-${
							isAlignmentEnd ? 'end' : 'start'
						}`]: isAlignmentEnd || breakpoint,
					},
					className,
				)}
				data-bs-popper={breakpoint ? 'static' : null}
				onMouseLeave={isCloseAfterLeave && setIsOpen ? () => setIsOpen(false) : undefined}
				{...props}
			>
				{children}
			</ul>
		);
	}
	return null;
};
DropdownMenu.displayName = 'DropdownMenu';

const ItemWrapper = forwardRef(({ children, className, ...props }, ref) => {
	return (
		<li ref={ref} className={classNames('dropdown-item-wrapper', className)} {...props}>
			{children}
		</li>
	);
});
ItemWrapper.displayName = 'ItemWrapper';

export const DropdownItem = forwardRef(
	({ children, isHeader, isDivider, isText, ...props }, ref) => {
		if (isHeader) {
			return (
				<ItemWrapper ref={ref} {...props}>
					{cloneElement(
						typeof children === 'string' ? <h6>{children}</h6> : children,
						{
							className: classNames('dropdown-header', children?.props?.className),
						},
					)}
				</ItemWrapper>
			);
		}
		if (isDivider) {
			return (
				<ItemWrapper ref={ref} {...props}>
					<hr className={classNames('dropdown-divider', children?.props?.className)} />
				</ItemWrapper>
			);
		}
		if (isText) {
			return (
				<ItemWrapper ref={ref} {...props}>
					{cloneElement(
						typeof children === 'string' ? <div>{children}</div> : children,
						{
							className: classNames(
								'dropdown-item-text',
								'dropdown-item',
								'disabled',
								children?.props?.className,
							),
						},
					)}
				</ItemWrapper>
			);
		}
		return (
			<ItemWrapper ref={ref} {...props}>
				{cloneElement(
					typeof children === 'string' ? <span>{children}</span> : children,
					{
						className: classNames('dropdown-item', children?.props?.className),
					},
				)}
			</ItemWrapper>
		);
	},
);
DropdownItem.displayName = 'DropdownItem';

const Dropdown = ({
	tag: Tag = 'div',
	children,
	isOpen,
	setIsOpen,
	direction = 'down',
	isButtonGroup,
	className,
}) => {
	const [state, setState] = useState(isOpen !== null && !!setIsOpen ? isOpen : false);

	const dropdownRef = useRef(null);
	const buttonRef = useRef(null);

	const closeMenu = useCallback(() => {
		if (isOpen !== null && !!setIsOpen) {
			setIsOpen(false);
		} else {
			setState(false);
		}
	}, [isOpen, setIsOpen]);

	useEventOutside(dropdownRef, 'mousedown', closeMenu);
	useEventOutside(dropdownRef, 'touchstart', closeMenu);

	return (
		<Tag
			ref={dropdownRef}
			className={classNames(
				{
					[`drop${direction}`]: direction && !isButtonGroup,
					'btn-group': isButtonGroup,
				},
				className,
			)}
		>
			{React.Children.map(children, (child, index) => {
				const type = child.type;
				const isTarget =
					typeof type === 'function' || typeof type === 'object'
						? ['DropdownMenu', 'DropdownToggle'].includes(type.displayName)
						: false;
				return isTarget
					? cloneElement(child, {
							isOpen: isOpen !== null && !!setIsOpen ? isOpen : state,
							setIsOpen: isOpen !== null && !!setIsOpen ? setIsOpen : setState,
							direction,
							key: index,
							buttonRef: buttonRef,
						})
					: child;
			})}
		</Tag>
	);
};
Dropdown.displayName = 'Dropdown';

export default Dropdown;
